<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div>
    <div
      v-if="!isSimpleView"
      class="floating-pane fading">
      <EstimatedDeliveryStatsHeader></EstimatedDeliveryStatsHeader>
      <div
        v-if="isInventoryFilterRequired"
        class="alert alert-info message-select-targeting">
        {{ capitalize($t('messages.itWillBecomeVisibleAsYouStartFiltering')) }}
      </div>
      <template v-else-if="isEntityValid">
        <div class="floating-pane-content">
          <div class="audience-info-kpis">
            <template v-if="audienceInfo != null">
              <template v-if="membershipSettings != null && membershipSettings.uiEnableAlphaFeatures">
                <KPI
                  v-show="isLineItemEntity"
                  :label="`${entity.budgetMode === 'B' ? capitalize($t('names.adSpend', { n: 1 })) : capitalize($t('names.impression', { n: 2 }))} goal`"
                  :value="
                    entity.budgetMode === 'B'
                      ? entity.cpmCalculator.budgetFormatted
                      : entity.cpmCalculator.estimatedTargetImpressionsFormatted
                  "
                  :icon="iconColoredBudget"
                  :is-loading="isLoadingAudienceInfo"
                  :big-icon="false"
                  value-style="font-size: 2.4rem;"
                  label-style="font-size: 1rem;" />
                <hr />
              </template>
              <EstimatedDeliveryStatsKpiImpressions
                :is-visible="audienceImpressions.isVisible"
                :label="capitalize($t('messages.averageAudienceConcentration'))"
                :value="audienceImpressions.value"
                :is-loading="isLoadingAudienceInfo"
                :icon="iconColoredDemographic" />
              <hr />
              <EstimatedDeliveryStatsKpiImpressions
                :is-visible="areStatsAvailable"
                :label="capitalize($t('messages.totalImpressionCapacity'))"
                :value="totalImpressions"
                :is-loading="isLoadingAudienceInfo"
                :icon="iconColoredImpressions" />
              <hr />
              <div class="flex-h">
                <EstimatedDeliveryStatsKpiUnits
                  :is-loading="isLoadingScreenAndSiteCount"
                  :unit-count="audienceInfo?.unitPhysicalCount"
                  :overridden-screen-count-tooltip-content="overriddenScreenCountTooltipContent"
                  :show-overridden-screen-count-warning="
                    showOverriddenScreenCountWarning
                  "></EstimatedDeliveryStatsKpiUnits>

                <div class="separator" />

                <EstimatedDeliveryStatsKpiSites
                  :is-loading="isLoadingScreenAndSiteCount"
                  :site-count="audienceInfo?.siteCount"></EstimatedDeliveryStatsKpiSites>
              </div>
              <hr />
              <reach-and-frequency-summary
                :entity="entity"
                :max-reach="audienceInfo?.impressions"
                :show-separator="true"
                :has-big-icon="false"
                value-style="font-size: 2.4rem;" />
              <hr />
              <template v-if="membershipSettings != null && membershipSettings.uiEnableAlphaFeatures">
                <div class="flex-h">
                  <EstimatedDeliveryStatsKpiImpressions
                    :is-visible="areStatsAvailable"
                    :label="capitalize($t('messages.totalImpressionCapacity'))"
                    :value="formatNumber(forecastedImpressions, '0.0a')"
                    :is-loading="isLoadingAudienceInfo"
                    :icon="iconColoredForecast" />
                  <div class="separator" />
                  <KPI
                    style="flex-basis: 38%"
                    :label="capitalize($t('messages.forecastedBidWinRate'))"
                    :value="formatNumber(forecastedBidWinRate, '%0.0')"
                    trend-format="+0a"
                    trend-style="font-size: 0.9em"
                    :icon="iconColoredWin"
                    :is-loading="isLoadingAudienceInfo"
                    :big-icon="false"
                    value-style="font-size: 2.4rem;"
                    label-style="font-size: 1rem;" />
                </div>
                <hr />
              </template>
            </template>
            <div class="cpm-info">
              <EstimatedDeliveryStatsKpiCpm
                :is-loading="isLoadingCPMInfo"
                :value="eCPM"></EstimatedDeliveryStatsKpiCpm>
              <div
                v-if="
                  entity.cpmCalculator.isCPMFromRateCard &&
                  cpmInfo != null &&
                  cpmInfo.histogram != null &&
                  cpmInfo.histogram.length > 1 &&
                  !isLoadingCPMInfo
                "
                class="cpm-histogram">
                <div
                  v-for="(item, i) in cpmInfo.histogram"
                  :key="i"
                  v-tooltip.left="
                    `${$n(item.start, 'currency')} - ${$n(item.end, 'currency')} (${item.count} ${$t('names.screen', item.count)})`
                  "
                  class="cpm-histogram-bar">
                  <div
                    v-if="item.count > 0"
                    class="bar"
                    :style="`height: ${formatNumber(item.count / cpmInfo.histogramMaxCount, '0%')};`" />
                </div>
              </div>
              <div
                v-if="isUsingMultipleDeals && !isLoadingCPMInfo"
                v-tooltip="capitalize($t('tooltips.ecpmNotAccurate'))"
                class="help">
                <icon-warning />
              </div>
            </div>
            <template v-if="cpmMargin">
              <hr />
              <div class="flex-h">
                <EstimatedDeliveryStatsKpi
                  class="text-nowrap"
                  :is-visible="true"
                  :label="capitalize($t('messages.advertiserCPM'))"
                  :value="advertiserCPM"
                  trend-format="+0a"
                  :icon="markRaw(IconColoredCPM)"
                  :is-loading="false" />
                <template v-if="advertiserMarkup">
                  <div class="separator" />
                  <EstimatedDeliveryStatsKpi
                    :is-visible="true"
                    :label="capitalize($t('names.markup'))"
                    :value="advertiserMarkup"
                    trend-format="+0a"
                    :icon="markRaw(IconColoredMarkup)"
                    :is-loading="false" />
                </template>
              </div>
            </template>
          </div>
        </div>
        <div
          v-if="closestCadence != null && isCheckingAvailsAvailable"
          class="floating-pane-content">
          <hr />
          <div class="flex-h">
            <KPI
              style="flex-basis: 20%"
              :label="closestCadence <= 1 ? $t('names.playPerHour', { n: 1 }) : $t('names.playPerHour', { n: 2 })"
              :value="`${closestCadence}`"
              trend-style="font-size: 0.9em"
              :icon="iconColoredPlayHexagon"
              :big-icon="false"
              value-style="font-size: 2.4rem;"
              label-style="font-size: 1rem;" />
            <div v-if="showRefreshCadenceBtn">
              <button
                class="btn btn-xs btn-primary-hollow mr-4"
                @click="$emit('clicked')">
                <icon-refresh />
                {{ $t('actions.refresh') }}
              </button>
            </div>
          </div>
        </div>
        <slot name="beforeFooter" />
        <div class="floating-pane-content-footer flex-v">
          <div class="disclaimer">
            <span
              v-if="audienceInfoAdDuration > 0 || (useLineItemAdDuration && adDuration != null && adDuration !== 0)">
              {{
                capitalize(
                  $t('messages.basedOnAdDurationOfX', [useLineItemAdDuration ? adDuration : audienceInfoAdDuration]),
                )
              }}
              <a
                v-if="!isAdDurationVisible && !useLineItemAdDuration"
                href="#"
                @click="showAdDuration"
                >{{ $t('actions.change') }}</a
              >
              <span v-if="!isAdDurationVisible && !useLineItemAdDuration"
                >|
                <a
                  href="#"
                  @click="audienceInfoAdDuration = 0"
                  >{{ $t('actions.reset') }}</a
                ></span
              >
            </span>
            <span v-else>
              {{ capitalize($t('messages.basedOnIndividualScreenAdDuration')) }}
              <a
                v-if="!isAdDurationVisible && !useLineItemAdDuration"
                href="#"
                @click="showAdDuration"
                >{{ $t('actions.change') }}</a
              >
            </span>
          </div>
          <div
            class="settings-row horizontal"
            :class="{ visible: isAdDurationVisible }">
            <div class="label">
              {{ capitalize($t('names.adDuration', { n: 1 })) }}
            </div>
            <div class="controls">
              <formatted-input
                ref="adDurationInput"
                name="adDuration"
                :is-money="false"
                format="0,0"
                :value="audienceInfoAdDuration"
                :suffix="$t('names.second', audienceInfoAdDuration)"
                @blur="isAdDurationVisible = false"
                @value-changed="
                  (x) => {
                    audienceInfoAdDuration = parseInt(x, 10);
                  }
                " />
            </div>
          </div>
        </div>
      </template>
      <div v-else>
        <div class="disclaimer">
          {{ capitalize($t('messages.selectStartAndEndDateToEnableEstimatedDeliveryStats')) }}
        </div>
      </div>
    </div>
    <template v-if="!isSimpleView && isEntityValid && showFeatureFees">
      <estimated-feature-fees
        v-if="featureKeys.length"
        :feature-keys="featureKeys" />
    </template>
    <div
      v-if="!isSimpleView && isEntityValid && showCreativeSizes"
      class="floating-pane fading">
      <div class="floating-pane-header">
        <icon-missing-dimension />
        <span>{{ capitalize($t('messages.recommendedCreativeSizes')) }}</span>
        <div class="actions">
          <div
            v-tooltip="capitalize($t('messages.recommendedCreativeSizesDescription'))"
            class="help">
            <icon-info />
          </div>
        </div>
      </div>
      <template v-if="true">
        <div class="floating-pane-content">
          <recommended-creative-sizes
            :is-loading="isLoadingAudienceInfo"
            :is-inventory-filter-required="isInventoryFilterRequired"
            :audience-info="audienceInfo" />
        </div>
      </template>
    </div>
    <simple-estimated-delivery-stats
      v-if="isPlanEntity && isSimpleView && (audienceInfo != null || isInventoryFilterRequired) && isEntityValid"
      :entity="entity as Plan"
      :audience-impressions="audienceImpressions"
      :are-impression-stats-available="areStatsAvailable"
      :is-loading-audience-info="isLoadingAudienceInfo || isLoadingGeofencesEstimatedDeliveryStatistics"
      :total-impressions="totalImpressions"
      :is-loading-screen-and-site-count="isLoadingScreenAndSiteCount || isLoadingGeofencesEstimatedDeliveryStatistics"
      :selected-type-count="selectedTypeCount"
      :is-loading-c-p-m-info="isLoadingCPMInfo || isLoadingGeofencesEstimatedDeliveryStatistics"
      :is-loading-geofences-estimated-delivery-statistics="isLoadingGeofencesEstimatedDeliveryStatistics"
      :e-c-p-m="eCPM"
      :is-inventory-filter-required="isInventoryFilterRequired" />
  </div>
</template>

<script setup lang="ts">
  import axios from 'axios';
  import {
    capitalize,
    computed,
    markRaw,
    onBeforeMount,
    onMounted,
    onUnmounted,
    ref,
    useTemplateRef,
    watch,
  } from 'vue';

  import { FormattedInput, RecommendedCreativeSizes } from '@/common/components/controls';
  import EstimatedDeliveryStatsHeader from '@/common/components/estimated-delivery-stats/EstimatedDeliveryStatsHeader.vue';
  import EstimatedDeliveryStatsKpi from '@/common/components/estimated-delivery-stats/EstimatedDeliveryStatsKpi.vue';
  import EstimatedDeliveryStatsKpiCpm from '@/common/components/estimated-delivery-stats/EstimatedDeliveryStatsKpiCpm.vue';
  import EstimatedDeliveryStatsKpiImpressions from '@/common/components/estimated-delivery-stats/EstimatedDeliveryStatsKpiImpressions.vue';
  import EstimatedDeliveryStatsKpiSites from '@/common/components/estimated-delivery-stats/EstimatedDeliveryStatsKpiSites.vue';
  import EstimatedDeliveryStatsKpiUnits from '@/common/components/estimated-delivery-stats/EstimatedDeliveryStatsKpiUnits.vue';
  import BudgetModes from '@/common/enums/budget-modes.enum';
  import FeeType from '@/common/enums/FeeType.enum';
  import { MapInventoryType } from '@/common/enums/map.enums';
  import PurchaseTypesEnum from '@/common/enums/purchase-types.enum';
  import { IconColoredCPM, IconColoredMarkup } from '@/common/icons/colored';
  import IconColoredBudget from '@/common/icons/colored/Budget.vue';
  import IconColoredDemographic from '@/common/icons/colored/Demographic.vue';
  import IconColoredForecast from '@/common/icons/colored/Forecast.vue';
  import IconColoredImpressions from '@/common/icons/colored/Impression.vue';
  import IconColoredPlayHexagon from '@/common/icons/colored/PlayHexagon.vue';
  import IconColoredWin from '@/common/icons/colored/Win.vue';
  import IconInfo from '@/common/icons/monochrome/Info.vue';
  import IconMissingDimension from '@/common/icons/monochrome/MissingDimension.vue';
  import IconRefresh from '@/common/icons/monochrome/Refresh.vue';
  import IconWarning from '@/common/icons/monochrome/Warning.vue';
  import { DeliveryStatistics } from '@/common/models';
  import Geofence from '@/common/models/geo/Geofence';
  import LineItem from '@/common/models/lineitem/LineItem';
  import MarginValue from '@/common/models/margins/MarginValue';
  import IFromPackage from '@/common/models/package/IFromPackage';
  import Package from '@/common/models/package/Package';
  import Plan from '@/common/models/plan/Plan';
  import { DASH } from '@/globals';
  import KPI from '@/org-app/components/controls/KPI.vue';
  import EstimatedFeatureFees from '@/org-app/components/lineItems/EstimatedFeatureFees.vue';
  import ReachAndFrequencySummary from '@/org-app/components/lineItems/ReachAndFrequencySummary.vue';
  import CpmUtils from '@/utils/CpmUtils';
  import MapDataProvider from '@/utils/MapDataProvider';
  import formatNumber from '@/utils/NumberFormatter';
  import {
    mapExchAppActions,
    mapGetters,
    mapOrgAppActions,
    mapOrgAppGetters,
    mapOrgAppState,
  } from '@/utils/StateMapping';
  import ThrottledTaskRunner from '@/utils/ThrottledTaskRunner';
  import i18n from '@/vueI18n';

  import AdDurationInput from './AdDurationInput.vue';
  import SimpleEstimatedDeliveryStats from './SimpleEstimatedDeliveryStats.vue';

  const DEBOUNCE_TIME = 500;

  type LineItemOrPlan = LineItem | Plan;

  interface IProps {
    entity?: LineItem | Package | Plan;
    index?: number;
    proximityGeofences?: Geofence[];
    useLineItemAdDuration?: boolean;
    showCreativeSizes?: boolean;
    showFeatureFees?: boolean;
    forecastedImpressions?: number;
    forecastedBidWinRate?: number;
    closestCadence?: number | null;
    cadenceECPM?: number | null;
    showRefreshCadenceBtn?: boolean;
    targetImpressions?: number | null;
    isSimpleView?: boolean;
    selectedTypeOfTargeting?: MapInventoryType;
    targetedUnitAndSiteIDsForGeofencesLoaded?: boolean | null;
    hasStartedFiltering?: boolean;
    isEditing?: boolean;
    sharedPlanUUID?: string;
  }

  const props = withDefaults(defineProps<IProps>(), {
    entity: null,
    index: 1,
    proximityGeofences: () => [],
    useLineItemAdDuration: false,
    showCreativeSizes: true,
    showFeatureFees: false,
    forecastedImpressions: 0,
    forecastedBidWinRate: 0,
    closestCadence: null,
    cadenceECPM: null,
    showRefreshCadenceBtn: false,
    targetImpressions: null,
    isSimpleView: false,
    selectedTypeOfTargeting: MapInventoryType.SITES,
    targetedUnitAndSiteIDsForGeofencesLoaded: null,
    hasStartedFiltering: false,
    isEditing: false,
    sharedPlanUUID: null,
  });

  const emits = defineEmits(['clicked', 'screenAndSiteCountChanged', 'statsChanged', 'seeUnitsConcentrationsModal']);

  const audienceInfo = ref(null);
  const audienceInfoAdDuration = ref(0);
  const cancelTokenSourceForRefreshAudienceInfo = ref(null);
  const cancelTokenSourceForRefreshCPMInfo = ref(null);
  const cpmInfo = ref(null);
  const isLoadingAudienceInfo = ref(false);
  const isLoadingCPMInfo = ref(false);
  const isLoadingScreenAndSiteCount = ref(false);
  const isAdDurationVisible = ref(false);
  const iconColoredBudget = markRaw(IconColoredBudget);
  const iconColoredDemographic = markRaw(IconColoredDemographic);
  const iconColoredForecast = markRaw(IconColoredForecast);
  const iconColoredImpressions = markRaw(IconColoredImpressions);
  const iconColoredWin = markRaw(IconColoredWin);
  const iconColoredPlayHexagon = markRaw(IconColoredPlayHexagon);
  const areStatsAvailable = ref(false);
  const lastCPMTargetParamsUsed = ref(null);
  const lastTargetParamsUsed = ref(null);
  const mapDataProvider = new MapDataProvider(props.entity);
  const isLoadingEstimatedDeliveryStatistics = ref(false);
  const refreshCPMInfoRunner = ref(null);
  const refreshAudienceInfoRunner = ref(null);
  const emitStatsChangedRunner = ref(null);
  const isInventoryFilterRequired = ref(false);
  const cancelTokenSourceForEstimatedDeliveryStatistics = ref(null);

  const adDurationInputRef = useTemplateRef<typeof AdDurationInput>('adDurationInput');

  //
  // Store mapping
  //
  const { hasChosenOrganization, hasChosenExchange, membershipSettings, targetedUnitsMax } = mapGetters();
  const { isDemandOrganization, isSupplyOrganization, isCuratorOrganization } = mapOrgAppGetters();
  const { organizationSettings } = mapOrgAppState();

  //
  // Computed properties
  //
  const isLineItemEntity = computed(() => props.entity instanceof LineItem);

  const isPackageEntity = computed(() => props.entity instanceof Package);

  const isPlanEntity = computed(() => props.entity instanceof Plan);

  const isEntityValid = computed(() => {
    return (
      (isLineItemEntity.value &&
        props.entity != null &&
        props.entity.startDate != null &&
        props.entity.endDate != null &&
        props.entity.endDate >= props.entity.startDate) ||
      (isPackageEntity.value &&
        props.entity.startDate != null &&
        props.entity.endDate != null &&
        props.entity.endDate >= props.entity.startDate &&
        (hasChosenOrganization.value || props.entity.mediaOwnerIDs.length > 0)) ||
      (isPlanEntity.value &&
        props.entity != null &&
        props.entity.startDate != null &&
        props.entity.endDate != null &&
        props.entity.endDate >= props.entity.startDate)
    );
  });

  const featureKeys = computed(() => {
    return isLineItemEntity.value
      ? [...(props.entity as LineItem).featureKeys, ...(props.entity as LineItem).transientFeatureKeys]
      : [];
  });

  const cpmMargin = computed<MarginValue>(() => {
    if (isLineItemEntity.value) {
      const lineItem = props.entity as LineItem;
      return lineItem.margins.find((m) => m.base === 'CPM') ?? null;
    }
    return null;
  });
  const advertiserCPM = computed<string>(() => {
    let value: number = 0;
    if (cpmMargin.value?.type.type === FeeType.Fixed) {
      value = cpmMargin.value.value;
    } else if (cpmMargin.value?.type.type === FeeType.FixedMarkup) {
      value = cpmMargin.value.value + props.entity.cpmCalculator.estimatedAverageCPM;
    } else if (cpmMargin.value?.type.type === FeeType.Percent) {
      value = props.entity.cpmCalculator.estimatedAverageCPM * (1 + cpmMargin.value.value);
    }
    return i18n.global.n(value, 'currency');
  });
  const advertiserMarkup = computed<string>(() => {
    if (cpmMargin.value?.type.type === FeeType.Fixed) {
      return null;
    }
    return cpmMargin.value?.valueFormatted;
  });

  const cpmTargetParams = computed(() => {
    const params = props.entity.cpmTargetParams;
    params.ad_duration = audienceInfoAdDuration.value;
    if (props.useLineItemAdDuration) {
      params.ad_duration = adDuration.value;
    }
    if (props.proximityGeofences.length > 0) {
      //TODO: Figure out if this is needed
      params.geofences = props.proximityGeofences.map((x) => x.geometry);
    }
    // When editing a Deal, we need to send -1, otherwise BE returns the previous targeting values already saved on the package
    if (props.isEditing && isPackageEntity.value) {
      params.package_id = -1;
    }
    return params;
  });

  const targetParamsEstimatedDeliveryStatistics = computed(() => {
    const params = props.entity.targetParamsEstimatedDeliveryStatistics;
    params.ad_duration = audienceInfoAdDuration.value;
    if (props.useLineItemAdDuration) {
      params.ad_duration = adDuration.value;
    }
    if (props.proximityGeofences.length > 0) {
      params.geofences = props.proximityGeofences.map((x) => x.geometry);
      params.unit_targeting = { ...params.unit_targeting, ...{ geofences: params.geofences } };
    }
    if (props.sharedPlanUUID != null) {
      params.isPrivate = false;
      params.planUuid = props.sharedPlanUUID;
    }

    // When editing a Deal, we need to send -1, otherwise BE returns the previous targeting values already saved on the package
    if (props.isEditing && isPackageEntity.value) {
      params.unit_targeting.package_id = -1;
    }
    return params;
  });

  const getCPMForTargets = computed(() => {
    if (hasChosenOrganization.value) {
      return orgGetCPMForTargets;
    }
    if (hasChosenExchange.value) return (params) => exchGetCPMForTargets({ params });
    return null;
  });

  const getEstimatedDeliveryStatisticsScreenAndSiteCount = computed(() => {
    if (hasChosenOrganization.value) {
      return orgGetEstimatedDeliveryStatisticsScreenAndSiteCount;
    }
    if (hasChosenExchange.value) return exchGetEstimatedDeliveryStatisticsScreenAndSiteCount;
    return null;
  });

  const getEstimatedDeliveryStatistics = computed(() => {
    if (hasChosenOrganization.value) {
      return orgGetEstimatedDeliveryStatistics;
    }
    if (hasChosenExchange.value) {
      return (params) => exchGetEstimatedDeliveryStatistics({ params });
    }
    return null;
  });

  const isBuyingFromExternalSSPDeal = computed(() => {
    return (
      isLineItemEntity.value &&
      ((props.entity as LineItem).dealIDProperty != null || (props.entity as LineItem).dealIDsProperty != null)
    );
  });

  const eCPM = computed(() => {
    let isSingleHivestackDeal = false;
    let isMultipleHivestackDeals = false;
    if (!isPackageEntity.value) {
      const fromPackage = props.entity as IFromPackage;
      isSingleHivestackDeal = fromPackage.package != null;
      isMultipleHivestackDeals =
        fromPackage.packageID != null ||
        fromPackage.packageGroupID != null ||
        fromPackage.packageGroupDealsIDs?.length > 0 ||
        fromPackage.packageIDs?.length > 0;
    }
    const isHivestackDeals = isSingleHivestackDeal || isMultipleHivestackDeals;

    // Used by PublicPlanMap.vue
    if (mapDataProvider.isPublicPlan) {
      return props.entity.eCPM;
    }

    if (isLineItemEntity.value && (props.entity as LineItem).isHouseAdEnabled) {
      // AD-266 - Don't show cpm for House Ads LI
      return DASH;
    }
    if (props.entity.purchaseType === PurchaseTypesEnum.CADENCE) {
      if (props.entity.budgetMode === BudgetModes.BUDGET) {
        return props.cadenceECPM != null ? i18n.global.n(props.cadenceECPM, 'currency') : DASH;
      }
      return !isPackageEntity.value && (props.entity as LineItemOrPlan).CPM != null
        ? i18n.global.n((props.entity as LineItemOrPlan).CPM, 'currency')
        : DASH;
    }
    if (
      !isHivestackDeals &&
      ((props.entity as LineItemOrPlan).CPM === 0 || isBuyingFromExternalSSPDeal.value) &&
      !(props.entity as Plan).hasMarkup
    ) {
      return DASH;
    }
    if (
      isPlanEntity.value &&
      isDemandOrganization.value &&
      (props.entity as Plan)?.hasMarkup &&
      (props.entity as Plan)?.isOverrideMarkup
    ) {
      return (props.entity as Plan).cpmMarkupFormatted;
    }
    return !props.entity.isCPMFromRateCard && (!isHivestackDeals || !isDemandOrganization.value)
      ? props.entity.cpmCalculator.discountedCPMFormatted
      : props.entity.cpmCalculator.discountedEstimatedCPMFormatted;
  });

  const isCheckingAvailsAvailable = computed(() => {
    return (
      isSupplyOrganization.value && organizationSettings != null && organizationSettings.uiEnableLineItemCheckAvails
    );
  });

  const audienceImpressions = computed(() => {
    if (audienceInfo.value == null) {
      return {
        isVisible: false,
        value: null,
      };
    }
    const { impressions, demographicImpressions } = audienceInfo.value;
    const isVisible = demographicImpressions !== impressions;

    if (!demographicImpressions) {
      return {
        isVisible: false,
        value: capitalize(i18n.global.t('messages.notCalculatedYet')),
      };
    }

    const concentration = demographicImpressions / impressions;

    return {
      isVisible,
      value: formatNumber(concentration, '0.00%'),
    };
  });

  const totalImpressions = computed(() => {
    if (!audienceInfo.value) return '0';
    const { impressions } = audienceInfo.value;
    return formatNumber(impressions, '0,0');
  });

  const siteCount = computed(() => {
    return formatNumber(audienceInfo.value?.siteCount, '0,0');
  });

  const unitPhysicalCountFormatted = computed(() => {
    if (!audienceInfo.value) return '0';
    const { unitPhysicalCount } = audienceInfo.value;
    return formatNumber(unitPhysicalCount, '0,0');
  });

  const selectedTypeCount = computed(() => {
    return props.selectedTypeOfTargeting === MapInventoryType.SITES
      ? {
          value: siteCount.value,
          label: i18n.global.t('names.site', { n: 2 }),
        }
      : {
          value: unitPhysicalCountFormatted.value,
          label: i18n.global.t('names.screen', { n: 2 }),
        };
  });

  const isUsingMultipleDeals = computed(() => {
    // We assume the entity is a Line Item
    return (
      (props.entity as LineItem).packageGroupID != null || (props.entity as LineItem).packageGroupDealsIDs?.length > 0
    );
  });

  const isLoadingGeofencesEstimatedDeliveryStatistics = computed(() => {
    return isLoadingEstimatedDeliveryStatistics.value && props.targetedUnitAndSiteIDsForGeofencesLoaded !== null;
  });

  const adDuration = computed(() => {
    if (isPackageEntity.value) {
      return (props.entity as Package).adDurationMin || 0;
    }
    return (props.entity as LineItemOrPlan).adDuration || 0;
  });

  const showOverriddenScreenCountWarning = computed(() => {
    const { unitCount, unitPhysicalCount } = audienceInfo.value;
    return unitPhysicalCount > 0 && unitPhysicalCount !== unitCount;
  });

  const overriddenScreenCountTooltipContent = computed(() => {
    const { unitCount, unitPhysicalCount } = audienceInfo.value;
    return capitalize(
      i18n.global.t('messages.physicalScreensInfo', [
        formatNumber(unitCount, '0,0'),
        formatNumber(unitPhysicalCount, '0,0'),
      ]),
    );
  });

  watch(
    () => [props.entity, props.proximityGeofences],
    () => {
      if (props.entity != null && props.proximityGeofences.length > 0) {
        refresh();
      }
    },
  );

  watch(audienceInfoAdDuration, () => {
    refresh();
  });
  watch(
    () => props.index,
    () => {
      refreshAudienceInfo();
    },
  );

  watch(
    () => props.targetedUnitAndSiteIDsForGeofencesLoaded,
    (val) => {
      if (!val) {
        isLoadingEstimatedDeliveryStatistics.value = true;
      } else if (val) {
        isLoadingEstimatedDeliveryStatistics.value = false;
        if (audienceInfo.value?.siteCount === 0) {
          refreshCPMInfo();
          refreshAudienceInfo();
        }
      }
    },
  );

  watch(
    () => props.hasStartedFiltering,
    (val) => {
      isInventoryFilterRequired.value = !val;
    },
  );

  onBeforeMount(() => {
    refreshCPMInfoRunner.value = new ThrottledTaskRunner(() => refreshCPMInfoInternal(), DEBOUNCE_TIME);
    refreshAudienceInfoRunner.value = new ThrottledTaskRunner(() => refreshAudienceInfoInternal(), DEBOUNCE_TIME);
    emitStatsChangedRunner.value = new ThrottledTaskRunner(
      (isCPMInfoUpdated: boolean = false) => emitStatsChanged(isCPMInfoUpdated),
      DEBOUNCE_TIME,
    );
  });

  onMounted(() => {
    refresh();
  });

  onUnmounted(() => {
    refreshAudienceInfoRunner.value.destroy();
    refreshCPMInfoRunner.value.destroy();
    emitStatsChangedRunner.value.destroy();
  });

  const orgGetCPMForTargets = mapOrgAppActions().getCPMForTargets;
  const orgGetEstimatedDeliveryStatistics = mapOrgAppActions().getEstimatedDeliveryStatistics;
  const orgGetEstimatedDeliveryStatisticsScreenAndSiteCount =
    mapOrgAppActions().getEstimatedDeliveryStatisticsScreenAndSiteCount;

  const exchGetCPMForTargets = mapExchAppActions().getCPMForTargets;
  const exchGetEstimatedDeliveryStatistics = mapExchAppActions().getEstimatedDeliveryStatistics;
  const exchGetEstimatedDeliveryStatisticsScreenAndSiteCount =
    mapExchAppActions().getEstimatedDeliveryStatisticsScreenAndSiteCount;

  async function refreshAudienceInfoInternal() {
    if (!isEntityValid.value) {
      return;
    }
    isLoadingAudienceInfo.value = true;
    isLoadingScreenAndSiteCount.value = true;

    if (cancelTokenSourceForRefreshAudienceInfo.value != null) {
      cancelTokenSourceForRefreshAudienceInfo.value.cancel();
    }

    cancelTokenSourceForRefreshAudienceInfo.value = axios.CancelToken.source();
    const data = targetParamsEstimatedDeliveryStatistics.value;
    const targetParams = {
      ...data,
      cancelTokenSource: cancelTokenSourceForRefreshAudienceInfo.value,
    };

    /**
     * Don't make the call if owner_ids is empty because
     * we're not allowed to show to the user all existing sites/screens we have in our platform
     */
    if (props.entity?.mediaOwnerIDs?.length <= 0) {
      cancelTokenSourceForRefreshAudienceInfo.value = null;
      isLoadingAudienceInfo.value = false;
      return;
    }
    try {
      const result = await getEstimatedDeliveryStatisticsScreenAndSiteCount.value({ params: targetParams });
      emits('screenAndSiteCountChanged', result);
      isLoadingScreenAndSiteCount.value = false;
      if (isCuratorOrganization.value) {
        audienceInfo.value = result.deliveryStatistics;
        cpmInfo.value = result.cpmInfo;
      } else {
        audienceInfo.value = result;
      }
      areStatsAvailable.value = result.unitCount <= targetedUnitsMax.value;
      if (areStatsAvailable.value) {
        //
        // When budget mode is cadence we need to calculate the impressions from 1 play / hour.
        //
        if (cancelTokenSourceForEstimatedDeliveryStatistics.value != null) {
          cancelTokenSourceForEstimatedDeliveryStatistics.value.cancel();
        }
        cancelTokenSourceForEstimatedDeliveryStatistics.value = axios.CancelToken.source();
        const stats: DeliveryStatistics = await getEstimatedDeliveryStatistics.value({
          params: targetParams,
          cancelTokenSource: cancelTokenSourceForEstimatedDeliveryStatistics.value,
        });
        if (props.entity.purchaseType === PurchaseTypesEnum.CADENCE) {
          const freqCap = targetParams.screen_frequency_cap;
          targetParams.screen_frequency_cap = 3600;
          const onePlayPerHourStats = await getEstimatedDeliveryStatistics.value({
            params: targetParams,
            cancelTokenSource: cancelTokenSourceForEstimatedDeliveryStatistics.value,
          });
          // Restore original freq. cap.
          targetParams.screen_frequency_cap = freqCap;
          stats.onePlayPerHourDemographicImpressions = onePlayPerHourStats.demographicImpressions;
          stats.onePlayPerHourImpressions = onePlayPerHourStats.impressions;
        }
        audienceInfo.value = stats;
      }
      emitStatsChangedRunner.value.run(true);
    } finally {
      cancelTokenSourceForRefreshAudienceInfo.value = null;
      isLoadingScreenAndSiteCount.value = false;
      isLoadingAudienceInfo.value = false;
    }
  }

  async function refreshAudienceInfo() {
    // Show loader while task is running
    isLoadingAudienceInfo.value = true;
    isLoadingScreenAndSiteCount.value = true;
    refreshAudienceInfoRunner.value.run();
  }

  async function refreshCPMInfoInternal() {
    if (
      !isEntityValid.value ||
      JSON.stringify(props.entity.cpmTargetParams) === JSON.stringify(lastCPMTargetParamsUsed.value)
    ) {
      return;
    }

    isLoadingCPMInfo.value = true;
    if (cancelTokenSourceForRefreshCPMInfo.value != null) {
      cancelTokenSourceForRefreshCPMInfo.value.cancel();
    }

    cancelTokenSourceForRefreshCPMInfo.value = axios.CancelToken.source();
    const params = {
      ...cpmTargetParams.value,
      cancelTokenSource: cancelTokenSourceForRefreshCPMInfo.value,
    };
    try {
      const result = await getCPMForTargets.value(params);
      cancelTokenSourceForRefreshCPMInfo.value = null;
      cpmInfo.value = CpmUtils.formatCpmInfo(result, isDemandOrganization.value);
      emitStatsChangedRunner.value.run(true);
    } finally {
      cancelTokenSourceForRefreshCPMInfo.value = null;
      isLoadingCPMInfo.value = false;
    }
  }

  function emitStatsChanged(isRefreshCPMInfo = false) {
    emits('statsChanged', {
      cpmInfo: cpmInfo.value,
      deliveryStatistics: audienceInfo.value,
      isRefreshCPMInfo,
    });
  }

  /**
   * Starts a refresh of the CPM data with a throttle of 500ms
   */
  function refreshCPMInfo() {
    // Show loader while task is running
    isLoadingCPMInfo.value = true;
    refreshCPMInfoRunner.value.run();
  }

  function showAdDuration() {
    isAdDurationVisible.value = true;
    adDurationInputRef.value.editValue();
  }

  async function refresh() {
    if (!isEntityValid.value) {
      return;
    }

    if (organizationSettings?.uiEnableSelectTargetingToSeeScreens && !props.hasStartedFiltering) {
      isInventoryFilterRequired.value = true;
      return;
    }
    isInventoryFilterRequired.value = false;

    //
    // Refresh audience info and CPM only if cpmTargetParams has changed
    //
    if (membershipSettings.value != null && lastCPMTargetParamsUsed.value !== JSON.stringify(cpmTargetParams.value)) {
      if (!isCuratorOrganization.value) {
        refreshCPMInfo();
      }
      refreshAudienceInfo();
    }

    //
    // Refresh audience info only if targetParams has changed
    //
    if (
      membershipSettings.value != null &&
      lastTargetParamsUsed.value !== JSON.stringify(targetParamsEstimatedDeliveryStatistics.value)
    ) {
      refreshAudienceInfo();
    }

    if (
      mapDataProvider.isPublicPlan &&
      lastTargetParamsUsed.value !== JSON.stringify(targetParamsEstimatedDeliveryStatistics.value)
    ) {
      if (cancelTokenSourceForEstimatedDeliveryStatistics.value != null) {
        cancelTokenSourceForEstimatedDeliveryStatistics.value.cancel();
      }

      cancelTokenSourceForEstimatedDeliveryStatistics.value = axios.CancelToken.source();
      audienceInfo.value = await mapDataProvider.getEstimatedDeliveryStatistics({
        params: targetParamsEstimatedDeliveryStatistics.value,
        cancelTokenSource: cancelTokenSourceForEstimatedDeliveryStatistics.value,
      });

      areStatsAvailable.value = audienceInfo.value.unitCount > 0;
    }

    lastCPMTargetParamsUsed.value = JSON.stringify(cpmTargetParams.value);
    lastTargetParamsUsed.value = JSON.stringify(targetParamsEstimatedDeliveryStatistics.value);
  }

  defineExpose({
    audienceImpressions,
    eCPM,
    targetParamsEstimatedDeliveryStatistics,
    totalImpressions,
    refresh,
    refreshCPMInfo,
    selectedTypeCount,
  });
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  .audience-info-kpis {
    .separator {
      width: 0.1rem;
      height: 5.2rem;
      border-left: dotted 0.1rem $borders;
      min-width: 0.1rem;
      max-width: 0.1rem;
    }

    .kpi.small {
      flex-basis: 0;
      flex-grow: 0;

      .right {
        .label {
          white-space: nowrap;
        }
      }

      .icon {
        svg {
          width: 3.2rem;
          height: 3.2rem;
        }
      }
    }
  }

  .cpm-info {
    display: flex;
    align-items: center;
    padding-right: 1.6rem;

    .kpi {
      flex-basis: auto;
    }

    .help {
      margin-left: 0.8rem;
      width: 1.6rem;
      height: 1.6rem;
      stroke: var(--textMutedColor);
      fill: var(--textMutedColor);
    }
  }

  .cpm-histogram {
    height: 4.3rem;
    display: flex;
    margin: 0.8rem 0;
    flex-grow: 1;

    .cpm-histogram-bar {
      margin: 0 1%;
      flex-grow: 1;
      position: relative;
      background: #eee;

      .bar {
        width: 100%;
        position: absolute;
        bottom: 0;
        min-height: 0.1rem;

        background-color: var(--primaryColor);
      }
    }
  }

  .layer-settings-pane {
    pointer-events: auto;
    position: absolute;
    box-shadow: 0 0 1rem rgba(77, 77, 77, 0.48);
    background-color: #fff;
    border-radius: 0.4rem;
    border: solid 0.1rem $borders;
    z-index: 3;
    font-size: 1.2rem;
    animation-name: scaleVertical;
    animation-duration: 0.5s;
    animation-timing-function: cubic-bezier(0.2, 1, 0.2, 1);
    animation-fill-mode: backwards;
    transform-origin: top;
    width: 45rem;

    .header {
      background-color: var(--paneHeaderBackgroundColor);

      padding: 0.8rem 1.6rem;
      border-bottom: solid 0.1rem $borders-light;
      font-weight: 600;
      border-radius: 0.4rem 0.4rem 0 0;
      display: flex;
      align-items: center;

      span {
        flex-grow: 1;
      }

      > svg {
        width: 1.8rem;
        height: 1.8rem;
        margin-right: 0.6rem;
      }
    }

    .content {
      padding: 0.8rem 1.6rem 1.6rem;

      .button-row {
        margin-bottom: 0.6rem;
        display: flex;

        .btn {
          flex-direction: column;
          align-items: center;
          text-align: center;
          flex-grow: 1;
        }
      }

      .advanced-settings {
        margin-bottom: 0.6rem;
        margin-top: 1.6rem;
        display: flex;
        align-items: center;
        cursor: pointer;

        svg {
          fill: var(--textMutedColor);
          stroke: var(--textMutedColor);

          width: 1.2rem;
          height: 1.2rem;
          margin-right: 0.3rem;
        }

        hr {
          display: none;
          flex-grow: 1;
          margin: 0 0 0 1rem;
        }

        &.expanded {
          hr {
            display: block;
          }
        }
      }
    }
  }

  .settings-row {
    font-size: 0.8em;
    transform: scaleY(0);
    transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);
    height: 0;

    &.visible {
      padding: 0.6rem 1.6rem;
      transform: scaleY(1);
      transform-origin: top;
      transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);
      height: auto;
    }

    &.horizontal {
      display: flex;
      align-items: center;

      .label {
        flex-grow: 1;
        margin-right: 1rem;
        margin-top: 0.6rem;
        width: auto;

        svg {
          height: 1.3rem;
          width: 1.3rem;

          fill: var(--textMutedColor);
          stroke: var(--textMutedColor);
        }
      }

      input[type='text'] {
        width: 26rem;
        padding: 0.6rem 0.8rem;
        font-size: 1.2rem;
      }
    }

    &:last-of-type {
      margin-bottom: 0;
    }

    .subsettings {
      border: solid 1px $borders-light;
      border-radius: 0.4rem;
      padding: 2.4rem 1.3rem 1rem 1rem;
      margin-top: 1.6rem;
      position: relative;

      .subsettings-header {
        background-color: var(--secondaryColor);
        color: var(--secondaryColorReverse);

        border: solid 1px rgba(0, 0, 0, 0.05);
        position: absolute;
        margin: auto;
        border-radius: 1rem;
        text-transform: uppercase;
        font-size: 1rem;
        font-weight: 900;
        padding: 0.2rem 0.8rem;
        top: -1rem;
        box-shadow: $small-shadow;
      }
    }

    label {
      margin-bottom: 0;
    }

    .label {
      width: 100%;
      font-weight: 700;
    }

    .controls {
      display: flex;
      justify-content: space-between;
      position: relative;
      flex-basis: 70%;

      input {
        flex-grow: 1;
        border-radius: 0.5rem;
        background: #fff;
        border: solid 0.1rem rgba(0, 0, 0, 0.15);
        padding: 0.2rem;
        /*text-align: center;*/
      }

      .color-swatch {
        width: 2rem;
        height: 1.6rem;
        border: solid 0.1rem #ffffff;
        box-shadow: 0 0.1rem 0.4rem #00000059;
        border-radius: 0.4rem;
        cursor: pointer;
      }

      .vc-chrome {
        position: absolute;
        z-index: 3;
        margin-top: 1.9rem;
        margin-left: 0.1rem;
        animation-name: scaleVertical;
        animation-duration: 0.5s;
        animation-timing-function: cubic-bezier(0.2, 1, 0.2, 1);
        animation-fill-mode: backwards;
        transform-origin: top;
      }

      .slider-text {
        width: 5rem;
        text-align: center;
        margin-right: 16px;
      }
    }

    .icon-preview {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 3.2rem;
      border: solid 0.1rem rgba(0, 0, 0, 0.1);
      background: rgb(240, 248, 255);
    }
  }

  .calculate-button {
    justify-content: center;
  }

  .refresh-button {
    padding: 0.5rem !important;
    margin-left: 1rem;
    position: relative;
    right: 2rem;
    border: 1px solid rgba(0, 0, 0, 0.15);

    background-color: #fff;
    color: var(--primaryColor);

    svg {
      width: 1.2rem;
      height: 1.2rem;
      margin-left: 0.5rem;

      stroke: var(--primaryColor);
      fill: var(--primaryColor);
    }
  }

  .message-select-targeting {
    margin: 1rem !important;
    font-size: 1.26rem;
  }

  .units-concentrations-button {
    margin: 0.6rem auto;
  }

  .ml-2.badge {
    svg {
      width: 1.2rem;
      height: 1.2rem;
      stroke: var(--primaryColor);
    }
  }
</style>
